import API from "./api";
import { toast } from "sonner";

/**
 * Helpers for viewing/downloading authenticated files.
 *
 * Stored file URLs look like "/api/files/{id}/{name}". Plain <a href> / <img src>
 * tags can't send the Bearer token, and the auth cookie expires after 1 hour,
 * so we fetch the bytes through our axios instance (which handles bearer token
 * + refresh) and hand the browser a local object URL.
 */

// Normalize a stored file URL to a path relative to the API base
// (axios baseURL already ends with "/api", so strip a leading "/api").
export function apiRelativePath(url) {
  if (!url) return url;
  return url.startsWith("/api/") ? url.slice(4) : url;
}

async function fetchBlobUrl(url) {
  const res = await API.get(apiRelativePath(url), { responseType: "blob" });
  return URL.createObjectURL(res.data);
}

/** Open an authenticated file in a new browser tab. */
export async function openAuthFile(url) {
  if (!url) return;
  if (/^(https?:|blob:|data:)/i.test(url)) {
    window.open(url, "_blank", "noopener");
    return;
  }
  try {
    const blobUrl = await fetchBlobUrl(url);
    const win = window.open(blobUrl, "_blank");
    if (!win) toast.error("Popup blocked — please allow popups to view files");
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
  } catch (e) {
    if (e?.response?.status === 410) {
      toast.error("This file's data is no longer on the server — please re-upload it.");
    } else {
      toast.error("Could not open file — it may have been removed.");
    }
  }
}

/** Download an authenticated file to the user's device. */
export async function downloadAuthFile(url, name) {
  if (!url) return;
  try {
    const blobUrl = await fetchBlobUrl(url);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = name || "download";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
  } catch {
    toast.error("Could not download file — it may have been removed.");
  }
}
